import React, { useState } from 'react';
import { X, Save, Loader2, AlertCircle } from 'lucide-react';
import { atlasApi, AtlasUser, Treinamento } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  treinamento?: Treinamento | null;
  onClose: () => void;
  onSalvo: () => void;
}

const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400';
const labelCls = 'text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block';

const ModalNovo: React.FC<Props> = ({ user, treinamento, onClose, onSalvo }) => {
  const ed = treinamento || null;
  const [form, setForm] = useState({
    titulo: ed?.titulo || '',
    descricao: ed?.descricao || '',
    categoria: ed?.categoria || '',
    modalidade: ed?.modalidade || 'online',
    carga_horaria: ed?.carga_horaria?.toString() || '',
    instrutor: ed?.instrutor || '',
    local: ed?.local || '',
    data_inicio: ed?.data_inicio?.slice(0, 10) || '',
    data_fim: ed?.data_fim?.slice(0, 10) || '',
    vagas: ed?.vagas?.toString() || '',
    obrigatorio: ed?.obrigatorio || false,
    status: ed?.status || 'rascunho',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const set = (k: keyof typeof form, v: any) => setForm(p => ({ ...p, [k]: v }));

  const salvar = async () => {
    if (!form.titulo.trim()) { setErro('Título é obrigatório.'); return; }
    setSalvando(true);
    setErro(null);
    const body: Record<string, any> = {
      ...form,
      carga_horaria: form.carga_horaria ? Number(form.carga_horaria) : undefined,
      vagas: form.vagas ? Number(form.vagas) : undefined,
    };
    try {
      if (ed) await atlasApi.editarTreinamento(user, ed._id, body);
      else await atlasApi.criarTreinamento(user, body);
      onSalvo();
    } catch (e: any) {
      setErro(e.message || 'Erro ao salvar.');
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-bold text-gray-900 flex-1">{ed ? 'Editar treinamento' : 'Novo treinamento'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className={labelCls}>Título *</label>
            <input className={inputCls} value={form.titulo} onChange={e => set('titulo', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Descrição</label>
            <textarea className={`${inputCls} min-h-[80px] resize-y`} value={form.descricao} onChange={e => set('descricao', e.target.value)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className={labelCls}>Categoria</label><input className={inputCls} value={form.categoria} onChange={e => set('categoria', e.target.value)} placeholder="Compliance, Técnico..." /></div>
            <div>
              <label className={labelCls}>Modalidade</label>
              <select className={inputCls} value={form.modalidade} onChange={e => set('modalidade', e.target.value)}>
                <option value="online">Online</option>
                <option value="presencial">Presencial</option>
                <option value="hibrido">Híbrido</option>
              </select>
            </div>
            <div><label className={labelCls}>Instrutor</label><input className={inputCls} value={form.instrutor} onChange={e => set('instrutor', e.target.value)} /></div>
            <div><label className={labelCls}>Local</label><input className={inputCls} value={form.local} onChange={e => set('local', e.target.value)} /></div>
            <div><label className={labelCls}>Carga horária (h)</label><input className={inputCls} type="number" value={form.carga_horaria} onChange={e => set('carga_horaria', e.target.value)} /></div>
            <div><label className={labelCls}>Vagas</label><input className={inputCls} type="number" value={form.vagas} onChange={e => set('vagas', e.target.value)} /></div>
            <div><label className={labelCls}>Início</label><input className={inputCls} type="date" value={form.data_inicio} onChange={e => set('data_inicio', e.target.value)} /></div>
            <div><label className={labelCls}>Fim</label><input className={inputCls} type="date" value={form.data_fim} onChange={e => set('data_fim', e.target.value)} /></div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="rascunho">Rascunho</option>
                <option value="publicado">Publicado</option>
                <option value="encerrado">Encerrado</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
              <input type="checkbox" checked={form.obrigatorio} onChange={e => set('obrigatorio', e.target.checked)} />
              Obrigatório
            </label>
          </div>

          {erro && <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm"><AlertCircle className="w-4 h-4" /> {erro}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalNovo;
